import { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Keyboard, ArrowCounterClockwise } from '@phosphor-icons/react';
import { useShortcutsStore, DEFAULT_BINDINGS, type RemappableAction } from '../../stores/shortcutsStore';

interface ShortcutsDialogProps {
  open: boolean;
  onClose: () => void;
}

// Detect OS to show platform-appropriate modifier key labels
const isMac = typeof navigator !== 'undefined' &&
  /Mac|iPad|iPhone|iPod/.test(navigator.userAgent ?? '');
const mod  = isMac ? '⌘' : 'Ctrl';
const alt  = isMac ? '⌥'  : 'Alt';

// Fixed shortcuts that are not remappable
const FIXED_GROUPS: { title: string; shortcuts: { keys: string[]; desc: string }[] }[] = [
  {
    title: 'Playback',
    shortcuts: [
      { keys: ['←', '→'],              desc: 'Step 1 frame (when paused)' },
      { keys: ['Shift', '←', '→'],     desc: 'Step 10 frames (when paused)' },
      { keys: ['J'],                    desc: 'Decrease playback speed' },
      { keys: ['K'],                    desc: 'Pause' },
      { keys: ['L'],                    desc: 'Increase playback speed' },
      { keys: ['I'],                    desc: 'Mark loop in point' },
      { keys: ['Shift', 'I'],           desc: 'Mark loop out point' },
    ],
  },
  {
    title: 'Canvas',
    shortcuts: [
      { keys: [mod, 'drag'],            desc: 'Temporary pan (any tool)' },
      { keys: ['Scroll'],               desc: 'Zoom in / out' },
    ],
  },
  {
    title: 'Layers',
    shortcuts: [
      { keys: ['Del', 'Backspace'],     desc: 'Delete selected layer(s)' },
      { keys: [mod, 'D'],               desc: 'Duplicate selected layer(s)' },
      { keys: ['['],                    desc: 'Decrease opacity 10%' },
      { keys: [']'],                    desc: 'Increase opacity 10%' },
      { keys: [mod, '['],               desc: 'Move layer up in stack' },
      { keys: [mod, ']'],               desc: 'Move layer down in stack' },
      { keys: [mod, 'click'],           desc: 'Add / remove from selection' },
      { keys: ['Shift', 'click'],       desc: 'Range select layers' },
    ],
  },
  {
    title: 'History',
    shortcuts: [
      { keys: [mod, 'Z'],               desc: 'Undo' },
      { keys: [mod, 'Y'],               desc: 'Redo' },
    ],
  },
  {
    title: 'Nudge (while playing)',
    shortcuts: [
      { keys: ['↑↓←→'],                desc: 'Nudge selected layer 1px' },
      { keys: ['Shift', '↑↓←→'],       desc: 'Nudge selected layer 10px' },
    ],
  },
  {
    title: 'Camera Bookmarks',
    shortcuts: [
      { keys: [mod, '1–9'],             desc: 'Save camera bookmark' },
      { keys: [alt, '1–9'],             desc: 'Recall camera bookmark' },
    ],
  },
  {
    title: 'File',
    shortcuts: [
      { keys: [mod, 'S'],               desc: 'Save scene' },
    ],
  },
  {
    title: 'Review',
    shortcuts: [
      { keys: ['R'],                    desc: 'Toggle Review panel' },
      { keys: ['Esc'],                  desc: 'Exit review draw mode' },
    ],
  },
];

function KeyBadge({ label }: { label: string }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[24px] h-[20px] px-1.5 rounded bg-bg-input border border-border text-[10px] font-mono text-text-primary font-semibold">
      {label}
    </kbd>
  );
}

// ----- Reference Tab -----
function ReferenceTab() {
  const { bindings } = useShortcutsStore();
  const grouped: Record<string, { action: RemappableAction; binding: typeof bindings[RemappableAction] }[]> = {};

  for (const [action, binding] of Object.entries(bindings) as [RemappableAction, typeof bindings[RemappableAction]][]) {
    if (!grouped[binding.group]) grouped[binding.group] = [];
    grouped[binding.group].push({ action, binding });
  }

  return (
    <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
      {/* Remappable shortcuts */}
      {Object.entries(grouped).map(([group, items]) => (
        <div key={group}>
          <h3 className="text-[10px] font-mono font-semibold text-text-tertiary uppercase tracking-widest mb-2">{group}</h3>
          <div className="space-y-1">
            {items.map(({ action, binding }) => (
              <div key={action} className="flex items-center justify-between gap-3">
                <span className="text-xs font-sans text-text-secondary">{binding.label}</span>
                <div className="flex items-center gap-1 shrink-0">
                  {binding.key ? <KeyBadge label={binding.display} /> : <span className="text-[10px] text-text-tertiary font-mono">—</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Fixed shortcuts */}
      {FIXED_GROUPS.map((group) => (
        <div key={group.title}>
          <h3 className="text-[10px] font-mono font-semibold text-text-tertiary uppercase tracking-widest mb-2">{group.title}</h3>
          <div className="space-y-1">
            {group.shortcuts.map((s) => (
              <div key={s.desc} className="flex items-center justify-between gap-3">
                <span className="text-xs font-sans text-text-secondary">{s.desc}</span>
                <div className="flex items-center gap-1 shrink-0">
                  {s.keys.map((k) => <KeyBadge key={k} label={k} />)}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ----- Customize Tab -----
function CustomizeTab() {
  const { bindings, setBinding, resetBinding, resetAll } = useShortcutsStore();
  const [capturing, setCapturing] = useState<RemappableAction | null>(null);

  useEffect(() => {
    if (!capturing) return;
    function onKey(e: KeyboardEvent) {
      e.preventDefault();
      if (e.key === 'Escape') { setCapturing(null); return; }
      const display = e.key === ' ' ? 'Space' : e.key.length === 1 ? e.key.toUpperCase() : e.code;
      const key = e.key === ' ' ? ' ' : e.key.toLowerCase();
      if (!capturing) return;
      setBinding(capturing, key, display);
      setCapturing(null);
    }
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [capturing, setBinding]);

  const grouped: Record<string, { action: RemappableAction; binding: typeof bindings[RemappableAction] }[]> = {};
  for (const [action, binding] of Object.entries(bindings) as [RemappableAction, typeof bindings[RemappableAction]][]) {
    if (!grouped[binding.group]) grouped[binding.group] = [];
    grouped[binding.group].push({ action, binding });
  }

  return (
    <div className="p-5 space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-sans text-text-tertiary">Click a binding to reassign it. Press a key to set, Esc to cancel.</p>
        <button
          onClick={resetAll}
          className="flex items-center gap-1 text-[10px] font-mono text-text-tertiary hover:text-text-primary px-2 py-1 rounded hover:bg-bg-hover transition-colors"
        >
          <ArrowCounterClockwise size={10} />
          Reset all
        </button>
      </div>

      {Object.entries(grouped).map(([group, items]) => (
        <div key={group}>
          <h3 className="text-[10px] font-mono font-semibold text-text-tertiary uppercase tracking-widest mb-2">{group}</h3>
          <div className="space-y-1">
            {items.map(({ action, binding }) => {
              const isCapturing = capturing === action;
              const isDefault = binding.key === DEFAULT_BINDINGS[action].key;
              return (
                <div key={action} className="flex items-center justify-between gap-3">
                  <span className="text-xs font-sans text-text-secondary">{binding.label}</span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      onClick={() => setCapturing(isCapturing ? null : action)}
                      className={`min-w-[52px] h-[22px] px-2 rounded border text-[10px] font-mono font-semibold transition-all ${
                        isCapturing
                          ? 'border-accent bg-accent-muted text-accent animate-pulse'
                          : binding.key
                          ? 'bg-bg-input border-border text-text-primary hover:border-accent/50'
                          : 'bg-bg-input border-border text-text-tertiary hover:border-accent/50'
                      }`}
                      title="Click to rebind"
                    >
                      {isCapturing ? 'Press key…' : (binding.key ? binding.display : '—')}
                    </button>
                    {!isDefault && (
                      <button
                        onClick={() => resetBinding(action)}
                        title="Reset to default"
                        className="text-text-tertiary hover:text-text-primary transition-colors"
                      >
                        <ArrowCounterClockwise size={10} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ShortcutsDialog({ open, onClose }: ShortcutsDialogProps) {
  const [tab, setTab] = useState<'reference' | 'customize'>('reference');

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/75 z-40" />
        <Dialog.Content
          data-testid="shortcuts-dialog"
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50
                     w-[min(620px,calc(100vw-32px))] max-h-[85vh] overflow-hidden flex flex-col
                     bg-bg-elevated border border-border rounded-xl shadow-lg focus:outline-none"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-border-subtle shrink-0">
            <div className="flex items-center gap-2">
              <Keyboard size={16} className="text-accent" />
              <Dialog.Title className="font-serif text-md font-medium text-text-primary">
                Keyboard Shortcuts
              </Dialog.Title>
              {isMac
                ? <span className="text-[9px] font-mono text-text-tertiary bg-bg-input border border-border px-1.5 py-0.5 rounded">macOS</span>
                : <span className="text-[9px] font-mono text-text-tertiary bg-bg-input border border-border px-1.5 py-0.5 rounded">Windows / Linux</span>
              }
            </div>
            <div className="flex items-center gap-2">
              {/* Tabs */}
              <div className="flex bg-bg-input rounded-md border border-border p-0.5">
                {(['reference', 'customize'] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => setTab(t)}
                    className={`px-3 py-1 rounded text-[11px] font-sans font-medium capitalize transition-colors ${
                      tab === t ? 'bg-accent text-white' : 'text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <Dialog.Close className="w-6 h-6 flex items-center justify-center rounded text-text-tertiary hover:text-text-primary hover:bg-bg-hover transition-all duration-fast">
                <X size={13} />
              </Dialog.Close>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto flex-1">
            {tab === 'reference' ? <ReferenceTab /> : <CustomizeTab />}
          </div>

          <div className="px-5 pb-4 border-t border-border-subtle pt-3 text-center shrink-0">
            <p className="text-[10.5px] font-mono text-text-tertiary">
              Press <KeyBadge label="?" /> anywhere to show this guide
            </p>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
